<script setup lang="ts">
// Imports
import QuestionAndAnswer from '~/components/partials/QuestionAndAnswer.vue';
import DownloadItem from '~/components/partials/DownloadItem.vue';
import useSwitchFilter from '~/utils/useSwitchFilter';
import Grid from '~/components/partials/Grid.vue';

// Types
import type {
  ContentFaqFragment,
  QuestionAndAnswerFragment,
  FaqCategoryFragment,
} from '#gql';

// Props
defineProps<{
  data: ContentFaqFragment;
}>();

// Data
const faqCategories = useNuxtApp().$faqCategories as FaqCategoryFragment[];

const { setActiveFilter, currentActiveFilterId } =
  useSwitchFilter(faqCategories);

const activeCategory = computed(() =>
  faqCategories.find((c) => c.id === currentActiveFilterId.value),
);
</script>

<template>
  <Grid class="content-faq">
    <div class="switchfilter switch-filter">
      <div class="inner">
        <button
          v-for="category in faqCategories"
          :key="category.id"
          class="filter text-xs-vl text-sm-vs"
          :class="{ '-active': currentActiveFilterId === category.id }"
          @click="setActiveFilter(category.id)"
        >
          {{ category.title }}
        </button>
      </div>
    </div>
    <div
      class="downloads"
      v-show="activeCategory?.content?.downloadList?.length"
      :class="{
        'is-hidden': !activeCategory?.content?.downloadList?.length,
      }"
    >
      <Transition name="fade" mode="out-in">
        <div class="transition" :key="currentActiveFilterId || 'default'">
          <DownloadItem
            v-for="item in activeCategory?.content?.downloadList || []"
            :key="item.id"
            :data="item"
          />
        </div>
      </Transition>
    </div>

    <div class="questions">
      <Transition name="fade" mode="out-in">
        <div
          class="transition"
          :key="`questions-${currentActiveFilterId || 'default'}`"
        >
          <QuestionAndAnswer
            v-for="item in data.faqItems.filter(
              (item: QuestionAndAnswerFragment) =>
                item.category?.id === currentActiveFilterId,
            )"
            :key="item.id"
            :data="item"
          />
        </div>
      </Transition>
    </div>
  </Grid>
</template>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(1rem);
}

.content-faq {
  padding: 0;

  @media (--vs) {
    display: flex;
    flex-direction: column;
    row-gap: 2.5rem;
  }

  @media (--vl) {
    row-gap: 4.5rem;
  }
}

.content-faq > .switchfilter,
.content-faq > .questions {
  grid-column: 2 / -2;
}

.content-faq > .questions > .transition {
  display: flex;
  flex-direction: column;

  @media (--vs) {
    gap: 1rem;
  }

  @media (--vl) {
    gap: 1.5rem;
  }
}

.content-faq > .downloads {
  grid-column: 2 / -2;

  // Compenate for gap when hidden
  &.is-hidden {
    @media (--vs) {
      margin-top: -2.5rem;
    }

    @media (--vl) {
      margin-top: -4.5rem;
    }
  }
}

.content-faq > .downloads > .transition {
  display: flex;
  flex-direction: column;

  @media (--vs) {
    gap: 1rem;
  }

  @media (--vl) {
    gap: 1.5rem;
  }
}
</style>
